import { BattleEngine, DEFAULT_RULES, ENGINE_VERSION } from '../engine/engine';
import type { BattleRules, ContentSnapshot } from '../engine/types';
import type { ReplayFile } from './replay';

export function playReplay(replay: ReplayFile, content: ContentSnapshot, rules: BattleRules = replay.rules ?? DEFAULT_RULES): BattleEngine {
  const exactVersion = replay.engineVersion === ENGINE_VERSION && replay.contentVersion === content.version;
  if (!exactVersion) throw new Error(`Unsupported replay versions engine=${replay.engineVersion}, content=${replay.contentVersion}`);
  const engine = new BattleEngine({ seed: replay.seed, content, arenaId: replay.arenaId, decks: replay.decks, rules, diagnosticProfile: 'standard', matchId: `replay-${replay.seed}`, ...(replay.botStrategy ? { botStrategy: replay.botStrategy } : {}) });
  const expectedCheckpoints = new Map(replay.checkpoints.map((checkpoint) => [checkpoint.tick, checkpoint.checksum]));
  const verifyCheckpoint = (): void => {
    const expected = expectedCheckpoints.get(engine.currentTick);
    const terminalCheckpointPending = replay.result?.tick === engine.currentTick && !engine.hasEnded;
    if (expected === undefined || terminalCheckpointPending) return;
    const actual = engine.checksum();
    if (expected !== actual) engine.journal.record({ tick: engine.currentTick, phase: 'simulation', source: 'replay-verifier', level: 'error', code: 'DET_CHECKSUM_MISMATCH', payload: { expected, actual } });
  };
  verifyCheckpoint();
  let commandIndex = 0;
  const finalTick = replay.result?.tick ?? Math.max(0, ...replay.commands.map((command) => command.tick)) + rules.normalTicks + rules.overtimeTicks + rules.countdownTicks + 1;
  while (!engine.hasEnded && engine.currentTick <= finalTick) {
    while (commandIndex < replay.commands.length && replay.commands[commandIndex]!.tick <= engine.currentTick) engine.enqueue(replay.commands[commandIndex++]!);
    engine.step();
    verifyCheckpoint();
  }
  const actualResult = engine.state.result;
  if (replay.result && JSON.stringify(actualResult) !== JSON.stringify(replay.result)) {
    engine.journal.record({ tick: engine.currentTick, phase: 'simulation', source: 'replay-verifier', level: 'error', code: 'REPLAY_RESULT_MISMATCH', payload: { expected: replay.result, actual: actualResult } });
  }
  return engine;
}
